// B-0143: sprite sheet layout, drawing and JSON atlas. Pure and canvas-agnostic so the same code runs in the browser
// and in tests (node-canvas). With padding 0 and extrude 0 the sheet is exactly the plain grid the tool always made.

export interface FrameSrc { name: string; width: number; height: number }
export interface SheetOptions { cols: number; padding: number; extrude: number }
export interface Placement { name: string; x: number; y: number; w: number; h: number; cellX: number; cellY: number }
export interface SheetPlan { width: number; height: number; cols: number; rows: number; cellW: number; cellH: number; padding: number; extrude: number; placements: Placement[] }

export const MAX_PADDING = 64;
export const MAX_EXTRUDE = 16;
/** Browsers refuse canvases past these limits (side length, total pixels). */
export const MAX_CANVAS_SIDE = 16384;
export const MAX_CANVAS_AREA = 268_435_456;

const whole = (raw: number, min: number, max: number) => Math.min(max, Math.max(min, Number.isFinite(raw) ? Math.floor(raw) : min));
/** Typed text to a whole number within limits; blank or junk gives `min`. */
export function parseWhole(text: string, min: number, max: number): number { return whole(parseInt(text, 10), min, max); }

/**
 * Cells are as large as the biggest frame plus the extruded border. Padding goes between cells only, so 4 frames of
 * 32 px in 4 columns with padding 2 make a 134 x 32 sheet. A smaller frame sits centred in its cell.
 */
export function planSheet(frames: FrameSrc[], o: SheetOptions): SheetPlan {
  const cols = Math.max(1, Math.floor(o.cols) || 1);
  const padding = whole(o.padding, 0, MAX_PADDING), extrude = whole(o.extrude, 0, MAX_EXTRUDE);
  const rows = Math.max(1, Math.ceil(frames.length / cols));
  const maxW = Math.max(1, ...frames.map((f) => f.width)), maxH = Math.max(1, ...frames.map((f) => f.height));
  const cellW = maxW + extrude * 2, cellH = maxH + extrude * 2;
  const placements = frames.map((f, i) => {
    const col = i % cols, row = Math.floor(i / cols);
    const cellX = col * (cellW + padding), cellY = row * (cellH + padding);
    return { name: f.name, w: f.width, h: f.height, cellX, cellY, x: cellX + extrude + Math.floor((maxW - f.width) / 2), y: cellY + extrude + Math.floor((maxH - f.height) / 2) };
  });
  // Like the plain grid always did, the sheet is `cols` cells wide even when there are fewer frames.
  return { width: cols * cellW + (cols - 1) * padding, height: rows * cellH + (rows - 1) * padding, cols, rows, cellW, cellH, padding, extrude, placements };
}

/** null when the sheet can be drawn, otherwise a plain explanation. */
export function sheetProblem(plan: Pick<SheetPlan, "width" | "height">): string | null {
  if (plan.width > MAX_CANVAS_SIDE || plan.height > MAX_CANVAS_SIDE || plan.width * plan.height > MAX_CANVAS_AREA)
    return `That sheet would be ${plan.width} x ${plan.height} px, more than a browser can draw (${MAX_CANVAS_SIDE} px on a side). Use more columns, fewer frames or smaller frames.`;
  return null;
}

type Ctx = {
  imageSmoothingEnabled: boolean;
  drawImage: (img: any, ...args: number[]) => void;
};

/** Draws every frame (and its extruded border) onto a context of the planned size. */
export function drawSheet(ctx: Ctx, plan: SheetPlan, imgs: unknown[]) {
  ctx.imageSmoothingEnabled = false; // smoothing would blur pixel art
  const e = plan.extrude;
  plan.placements.forEach((p, i) => {
    const img = imgs[i];
    ctx.drawImage(img, p.x, p.y);
    if (!e) return;
    // Repeat the outermost pixel row/column outwards: stops neighbours bleeding in when the engine filters or mip-maps.
    ctx.drawImage(img, 0, 0, 1, p.h, p.x - e, p.y, e, p.h);
    ctx.drawImage(img, p.w - 1, 0, 1, p.h, p.x + p.w, p.y, e, p.h);
    ctx.drawImage(img, 0, 0, p.w, 1, p.x, p.y - e, p.w, e);
    ctx.drawImage(img, 0, p.h - 1, p.w, 1, p.x, p.y + p.h, p.w, e);
    ctx.drawImage(img, 0, 0, 1, 1, p.x - e, p.y - e, e, e);
    ctx.drawImage(img, p.w - 1, 0, 1, 1, p.x + p.w, p.y - e, e, e);
    ctx.drawImage(img, 0, p.h - 1, 1, 1, p.x - e, p.y + p.h, e, e);
    ctx.drawImage(img, p.w - 1, p.h - 1, 1, 1, p.x + p.w, p.y + p.h, e, e);
  });
}

/** Unique, file-name-like frame names (duplicates get -2, -3 ...). */
export function frameNames(names: string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((n, i) => {
    const base = n.trim() || `frame_${String(i + 1).padStart(2, "0")}.png`;
    const k = (seen.get(base) ?? 0) + 1;
    seen.set(base, k);
    return k === 1 ? base : base.replace(/(\.[^.]*)?$/, (ext) => `-${k}${ext}`);
  });
}

/**
 * TexturePacker "JSON (Hash)" layout, read by Phaser, PixiJS and many other engines: each frame's x, y, w, h in the
 * sheet. Frames are never rotated or trimmed here, so spriteSourceSize is the frame itself.
 */
export function atlasJson(plan: SheetPlan, imageName: string) {
  const frames: Record<string, unknown> = {};
  for (const p of plan.placements) {
    frames[p.name] = {
      frame: { x: p.x, y: p.y, w: p.w, h: p.h },
      rotated: false,
      trimmed: false,
      spriteSourceSize: { x: 0, y: 0, w: p.w, h: p.h },
      sourceSize: { w: p.w, h: p.h },
    };
  }
  return { frames, meta: { app: "FreeDailyPro Sprite Sheet", image: imageName, format: "RGBA8888", size: { w: plan.width, h: plan.height }, scale: "1", padding: plan.padding, extrude: plan.extrude } };
}

export interface SplitCell { x: number; y: number; w: number; h: number }
/** Cells of a sheet cut into cols x rows with `padding` px between cells (use the padding the sheet was made with). */
export function splitCells(width: number, height: number, cols: number, rows: number, padding = 0): { cells: SplitCell[]; exact: boolean } {
  const c = Math.max(1, Math.floor(cols) || 1), r = Math.max(1, Math.floor(rows) || 1), pad = whole(padding, 0, MAX_PADDING);
  const fw = (width - pad * (c - 1)) / c, fh = (height - pad * (r - 1)) / r;
  const cells: SplitCell[] = [];
  for (let row = 0; row < r; row++) for (let col = 0; col < c; col++) cells.push({ x: Math.round(col * (fw + pad)), y: Math.round(row * (fh + pad)), w: Math.max(1, Math.floor(fw)), h: Math.max(1, Math.floor(fh)) });
  return { cells, exact: Number.isInteger(fw) && Number.isInteger(fh) && fw > 0 && fh > 0 };
}

export const framesLabel = (n: number) => `${n} ${n === 1 ? "frame" : "frames"}`;
